import type { ChatType } from '../../services/chat';

const BUTTON_BASE =
    'inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-all';
const IDLE_CLASSES = 'text-gray-500 hover:text-gray-700';

interface UnreadDotProps {
    label: string;
}

function UnreadDot({ label }: UnreadDotProps) {
    return (
        <span
            role="status"
            aria-label={label}
            className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"
        />
    );
}

interface ChatModeToggleProps {
    value: ChatType;
    onChange: (mode: ChatType) => void;
    /** Internal chat lives on the ortho board item, so it is off until the case has one. */
    internalDisabled: boolean;
    hasUnreadExternal: boolean;
    hasUnreadInternal: boolean;
}

// External (practice) / Internal (staff) switch shown above a patient's conversation.
export function ChatModeToggle({
    value,
    onChange,
    internalDisabled,
    hasUnreadExternal,
    hasUnreadInternal
}: ChatModeToggleProps) {
    const externalTone = value === 'external' ? 'bg-[#3773A8] text-white shadow-sm' : IDLE_CLASSES;
    const internalTone = internalDisabled
        ? 'text-gray-300 cursor-not-allowed'
        : value === 'internal'
          ? 'bg-[#800020] text-white shadow-sm'
          : IDLE_CLASSES;
    return (
        <div className="flex items-center justify-center px-4 py-1.5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                    type="button"
                    onClick={() => onChange('external')}
                    className={`${BUTTON_BASE} ${externalTone}`}
                >
                    External
                    {hasUnreadExternal && <UnreadDot label="Unread external message" />}
                </button>
                <button
                    type="button"
                    disabled={internalDisabled}
                    onClick={() => onChange('internal')}
                    className={`${BUTTON_BASE} ${internalTone}`}
                >
                    Internal
                    {hasUnreadInternal && !internalDisabled && (
                        <UnreadDot label="Unread internal message" />
                    )}
                </button>
            </div>
        </div>
    );
}
